import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { GetWasteShipmentQuery, WasteShipmentStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";

import { ManifestDrawer } from "../Manifests/ManifestDetailDrawer";
import {
  ShippingRecordUploadFlow,
  UploadableShippingKind,
} from "../Manifests/ShippingRecordUploadFlow";
import {
  ATTACH_SHIPMENT_MANIFEST_MUTATION,
  DETACH_SHIPMENT_MANIFEST_MUTATION,
  MANIFEST_LINK_REFETCH_QUERIES,
} from "./api";
import { AttachManifestDialog } from "./AttachManifestDialog";
import { DetachManifestDialog } from "./DetachManifestDialog";
import { ManifestStepActions } from "./ManifestStepActions";
import { attachedDocumentTitle } from "./reconciliationDisplay";

/**
 * Only the fields this section and its dialogs read, picked from the query
 * result rather than restated: a rename in the query still breaks here, and the
 * prop says what the component actually depends on. Taking the whole shipment
 * left a test no way to build a fixture except casting past the contract.
 */
type Shipment = Pick<
  NonNullable<GetWasteShipmentQuery["wasteShipment"]>,
  "id" | "facilityId" | "status" | "manifestSigned" | "manifest" | "reconciliation"
>;

/**
 * The mutations that change which manifest the shipment is linked to, with
 * their alerts. They all report failure the same way and none has any bearing
 * on the dialogs' open state, so they sit together and away from it.
 */
function useAttachedDocumentActions(shipment: Shipment) {
  const alerts = useAlerts();
  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });
  const [detachManifestMutation, { loading: detachingManifest }] = useMutation(
    DETACH_SHIPMENT_MANIFEST_MUTATION,
    { refetchQueries: MANIFEST_LINK_REFETCH_QUERIES }
  );

  const setSigned = async (signed: boolean) => {
    const manifestId = shipment.manifest?.id;
    if (!manifestId) {
      return;
    }
    try {
      // Re-attaching the manifest already linked is the signed-copy flow; the
      // server keeps the original received date.
      await attach({
        variables: { input: { shipmentId: shipment.id, processedManifestId: manifestId, signed } },
      });
      alerts.success(signed ? "Marked as the signed copy." : "Marked as an unsigned draft.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the manifest.");
    }
  };

  /** Resolves true when the link was actually released, so the caller can close. */
  const detachDocument = async (): Promise<boolean> => {
    try {
      await detachManifestMutation({ variables: { shipmentId: shipment.id } });
      alerts.success(
        `${attachedDocumentTitle(shipment.reconciliation.attachedDocumentKind)} detached.`
      );
      return true;
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to detach the document.");
      return false;
    }
  };

  return { attaching, detachingManifest, setSigned, detachDocument };
}

/**
 * Which dialog is open, and whether the picker it opens is replacing something.
 *
 * Split out because these say nothing about the shipment, which leaves the
 * component body as the render and the facts it renders from.
 */
function useDocumentDialogState() {
  const [manifestPickerOpen, setManifestPickerOpen] = useState(false);
  const [replacingManifest, setReplacingManifest] = useState(false);
  // The kind outlives the upload dialog: the analyzing dialog dismisses it right
  // after handing off to review, and the review step lives in the same flow.
  const [uploadKind, setUploadKind] = useState<UploadableShippingKind | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detachOpen, setDetachOpen] = useState(false);
  // The attached manifest, read over this shipment rather than in a tab beside
  // it: comparing the two is the whole reason to open it.
  const [manifestDrawerOpen, setManifestDrawerOpen] = useState(false);

  const openManifestPicker = (replacing: boolean) => {
    setReplacingManifest(replacing);
    setManifestPickerOpen(true);
  };

  return {
    manifestPickerOpen,
    replacingManifest,
    uploadKind,
    uploadOpen,
    detachOpen,
    manifestDrawerOpen,
    openManifestPicker,
    closeManifestPicker: () => setManifestPickerOpen(false),
    openUpload: (kind: UploadableShippingKind) => {
      setUploadKind(kind);
      setUploadOpen(true);
    },
    closeUpload: () => setUploadOpen(false),
    openDetach: () => setDetachOpen(true),
    closeDetach: () => setDetachOpen(false),
    openManifestDrawer: () => setManifestDrawerOpen(true),
    closeManifestDrawer: () => setManifestDrawerOpen(false),
  };
}

/**
 * Every dialog and drawer this section can open, behind one boundary, so the
 * section's own job (which manifest is attached, and what can be done to it)
 * stays readable.
 */
function PaperworkDialogs({
  shipment,
  dialogs,
  tenantId,
  canUploadBillOfLading,
  trackingNumber,
  detachingManifest,
  onDetach,
}: {
  shipment: Shipment;
  dialogs: ReturnType<typeof useDocumentDialogState>;
  tenantId: string | undefined;
  canUploadBillOfLading: boolean;
  trackingNumber: string | null;
  detachingManifest: boolean;
  onDetach: () => void;
}) {
  return (
    <>
      <ManifestDrawer
        processedManifestId={shipment.manifest?.id}
        open={dialogs.manifestDrawerOpen}
        onClose={dialogs.closeManifestDrawer}
      />

      {dialogs.manifestPickerOpen && (
        <AttachManifestDialog
          shipmentId={shipment.id}
          facilityId={shipment.facilityId}
          replacingTrackingNumber={dialogs.replacingManifest ? trackingNumber : null}
          onClose={dialogs.closeManifestPicker}
          // Hand off rather than stack: the picker has served its purpose once
          // the reader knows the manifest is not in it. Replacing is exempt —
          // that swaps in another manifest Encamp already has.
          canUploadBillOfLading={canUploadBillOfLading}
          onUploadInstead={
            dialogs.replacingManifest
              ? undefined
              : (kind) => {
                  dialogs.closeManifestPicker();
                  dialogs.openUpload(kind);
                }
          }
        />
      )}
      {dialogs.uploadKind && tenantId && (
        <ShippingRecordUploadFlow
          documentKind={dialogs.uploadKind}
          open={dialogs.uploadOpen}
          onClose={dialogs.closeUpload}
          tenantId={tenantId}
          facilityId={shipment.facilityId}
        />
      )}
      {dialogs.detachOpen && (
        <DetachManifestDialog
          trackingNumber={trackingNumber}
          documentKind={shipment.reconciliation.attachedDocumentKind ?? null}
          detaching={detachingManifest}
          onConfirm={onDetach}
          onClose={dialogs.closeDetach}
        />
      )}
    </>
  );
}

/**
 * The official paper for a shipment, a manifest that may be a bill of lading,
 * and how it compares to what was requested.
 *
 * Attaching is independent of the shipment's own progress — the vendor's copy
 * can arrive before or after the load leaves — so this section is live in every
 * status except canceled, and never gates or is gated by "Mark shipped".
 *
 * A difference here is not an error. The vendor re-weighs at the scale,
 * re-classifies, splits a line; the attached document is the regulated record
 * and the request was an estimate. So the panel reports rather than blocks, and
 * the severity comes from the same scale the manifest discrepancy views use.
 */
export function ShipmentManifestSection({
  shipment,
  attachBlockedReason,
}: {
  shipment: Shipment;
  /**
   * Why a document cannot be attached right now, or undefined when it can.
   *
   * Staged container changes are the case: attaching freezes the contents, so
   * the save that was about to commit them would be refused and the edit could
   * only be thrown away. Detaching is deliberately not blocked — it unlocks the
   * contents, so it is the way out rather than a way in.
   */
  attachBlockedReason?: string;
}) {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const dialogs = useDocumentDialogState();

  const { attaching, detachingManifest, setSigned, detachDocument } =
    useAttachedDocumentActions(shipment);

  const reconciliation = shipment.reconciliation;
  const attachedKind = reconciliation.attachedDocumentKind ?? null;
  const isManifestAttached = attachedKind != null;

  const trackingNumber =
    reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;

  // Null while a manifest link dangles: there is a tracking number from the
  // snapshot but no manifest row to open yet.
  const openAttachedDocument =
    isManifestAttached && shipment.manifest ? dialogs.openManifestDrawer : null;

  const handleDetach = async () => {
    if (await detachDocument()) {
      dialogs.closeDetach();
    }
  };

  // A canceled shipment can take no paperwork, and its step already renders
  // inactive and greyed with nothing under it — which reads as "not applicable"
  // without a sentence saying so.
  if (shipment.status === WasteShipmentStatus.Canceled) {
    return null;
  }

  return (
    <>
      <ManifestStepActions
        attachedKind={attachedKind}
        documentNumber={trackingNumber}
        // Opens in a drawer over this page, because the reason to open the
        // paperwork is to read it against the shipment: a route that navigates
        // away costs the reader the thing they are comparing it to.
        onOpenDocument={openAttachedDocument}
        signed={shipment.manifestSigned}
        dangling={isManifestAttached && shipment.manifest == null}
        attachBlockedReason={attaching ? "Attaching…" : attachBlockedReason}
        signedChangePending={attaching}
        onAttachManifest={() => dialogs.openManifestPicker(isManifestAttached)}
        onRelink={() => dialogs.openManifestPicker(true)}
        onDetach={dialogs.openDetach}
        onSignedChange={setSigned}
      />

      <PaperworkDialogs
        shipment={shipment}
        dialogs={dialogs}
        tenantId={tenantId}
        canUploadBillOfLading={!!featureFlags[FeatureFlagKey.ManifestViewsV2]}
        trackingNumber={trackingNumber}
        detachingManifest={detachingManifest}
        onDetach={handleDetach}
      />
    </>
  );
}
